import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, finalize, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  StaffUserApiService,
  type StaffUserPermission,
  StaffUserRow,
} from 'src/app/core/services/staff-user-api.service';
import { AgentApiService, type AgentRecord } from 'src/app/core/services/agent-api.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { AppDatePipe } from 'src/app/shared/pipes/app-date.pipe';
import { TenantPhonePipe } from 'src/app/shared/pipes/tenant-phone.pipe';
import { adminUserEditPath, isAdminUserUuid } from '../admin-user-route.util';
import { isSuperAdminRoleSlug } from 'src/app/core/constants/super-admin-role';
import {
  ROLE_PERMISSION_ATTACH_SLUGS,
  SUPER_ADMIN_PERMISSION_SLUGS,
} from 'src/app/core/constants/super-admin-permission-slugs';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { AuditHistoryComponent } from 'src/app/shared/audit-history/audit-history.component';

interface UserPermissionGroup {
  module: string;
  module_slug: string;
  permissions: StaffUserPermission[];
  expanded: boolean;
}

@Component({
  selector: 'app-admin-user-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    AppDatePipe,
    TenantPhonePipe,
    HasPermissionPipe,
    AuditHistoryComponent,
  ],
  templateUrl: './admin-user-detail.component.html',
  styleUrl: './admin-user-detail.component.css',
})
export class AdminUserDetailComponent implements OnInit, OnDestroy {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;
  readonly roleAttachSlugs: string[] = [...ROLE_PERMISSION_ATTACH_SLUGS];

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userApi = inject(StaffUserApiService);
  private readonly agentApi = inject(AgentApiService);
  private readonly destroy$ = new Subject<void>();

  userUuid = '';
  userId = 0;
  loading = true;
  agentsLoading = false;
  loadError = '';
  user: StaffUserRow | null = null;
  agents: AgentRecord[] = [];
  agentsTotal = 0;
  permissionGroups: UserPermissionGroup[] = [];
  permissionsTotal = 0;
  isSuperAdminUser = false;

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const uuid = (params.get('uuid') ?? '').trim();
      if (!isAdminUserUuid(uuid)) {
        this.loadError = 'Invalid user link.';
        this.loading = false;
        return;
      }
      this.userUuid = uuid;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get allPermissionGroupsExpanded(): boolean {
    return (
      this.permissionGroups.length > 0 && this.permissionGroups.every((g) => g.expanded)
    );
  }

  togglePermissionGroup(group: UserPermissionGroup): void {
    group.expanded = !group.expanded;
  }

  expandAllPermissionGroups(): void {
    for (const g of this.permissionGroups) {
      g.expanded = true;
    }
  }

  collapseAllPermissionGroups(): void {
    for (const g of this.permissionGroups) {
      g.expanded = false;
    }
  }

  toggleAllPermissionGroups(): void {
    if (this.allPermissionGroupsExpanded) {
      this.collapseAllPermissionGroups();
    } else {
      this.expandAllPermissionGroups();
    }
  }

  load(): void {
    this.loading = true;
    this.loadError = '';
    this.user = null;
    this.agents = [];

    this.userApi
      .getUserByUuid(this.userUuid)
      .pipe(assertSuccessEnvelope(), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          if (!env.data) {
            this.loadError = 'User not found.';
            this.loading = false;
            return;
          }
          this.user = env.data;
          this.userId = env.data.id;
          if (env.data.uuid?.trim()) {
            this.userUuid = env.data.uuid.trim();
          }
          this.buildPermissionGroups();
          this.loading = false;
          this.loadAgents();
        },
        error: (err) => {
          this.loading = false;
          this.loadError = resolveApiErrorMessage(err) || 'Could not load user details.';
        },
      });
  }

  private loadAgents(): void {
    this.agentsLoading = true;
    this.agentApi
      .listAgents({ created_by: this.userId, page: 1, page_size: 100 })
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.agentsLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (env) => {
          this.agents = env.data?.items ?? [];
          this.agentsTotal = env.data?.total ?? this.agents.length;
        },
        error: () => {
          this.agents = [];
          this.agentsTotal = 0;
        },
      });
  }

  profileImageUrl(): string | null {
    return this.profilePreview(this.user);
  }

  private profilePreview(row: StaffUserRow | null): string | null {
    if (!row?.profile_picture) return null;
    const pp = row.profile_picture;
    if (typeof pp === 'string') return pp;
    if (typeof pp === 'object' && pp !== null) {
      const o = pp as Record<string, unknown>;
      if (typeof o['preview_url'] === 'string') return o['preview_url'];
    }
    return null;
  }

  initials(name: string | null | undefined): string {
    const n = (name ?? '').trim();
    return n ? n.charAt(0).toUpperCase() : 'U';
  }

  roleLabel(): string | null {
    const role = this.user?.user_roles?.[0]?.role;
    return role?.role_name ?? role?.slug ?? null;
  }

  roleId(): number | null {
    const id = this.user?.user_roles?.[0]?.role?.id ?? this.user?.user_roles?.[0]?.role_id;
    return typeof id === 'number' && id > 0 ? id : null;
  }

  permissionDisplayName(p: StaffUserPermission): string {
    return (p.permissions_name ?? '').trim() || p.slug;
  }

  goToManageRolePermissions(): void {
    const roleId = this.roleId();
    if (!roleId) return;
    const role = this.user?.user_roles?.[0]?.role;
    void this.router.navigate(['/system-preferences/manage-permissions', roleId], {
      state: role ? { role: { id: role.id, role_name: role.role_name, slug: role.slug, description: null, is_active: true } } : undefined,
    });
  }

  private buildPermissionGroups(): void {
    this.permissionGroups = [];
    this.permissionsTotal = 0;
    this.isSuperAdminUser = false;

    const roles = this.user?.user_roles ?? [];
    for (const ur of roles) {
      if (isSuperAdminRoleSlug(ur.role?.slug)) {
        this.isSuperAdminUser = true;
        return;
      }
    }

    const byId = new Map<number, StaffUserPermission>();
    for (const ur of roles) {
      const links = ur.role?.role_permissions ?? [];
      for (const link of links) {
        const p = link.permission;
        if (!p?.id || byId.has(p.id)) continue;
        if (p.is_deleted || p.is_active === false) continue;
        byId.set(p.id, p);
      }
    }

    const grouped = new Map<string, StaffUserPermission[]>();
    for (const p of byId.values()) {
      const key = (p.model ?? 'other').trim() || 'other';
      const list = grouped.get(key) ?? [];
      list.push(p);
      grouped.set(key, list);
    }

    const sortedGroups = [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
    this.permissionGroups = sortedGroups.map(([module_slug, permissions]) => {
      const sortedPermissions = [...permissions];
      sortedPermissions.sort((x, y) =>
        this.permissionDisplayName(x).localeCompare(this.permissionDisplayName(y)),
      );
      return {
        module_slug,
        module: this.formatModuleName(module_slug),
        permissions: sortedPermissions,
        expanded: false,
      };
    });

    this.permissionsTotal = byId.size;
    if (this.permissionGroups.length === 1) {
      this.permissionGroups[0].expanded = true;
    }
  }

  private formatModuleName(key: string): string {
    if (key === 'other') return 'Other';
    return key.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  editUserLink(): string[] | null {
    return adminUserEditPath(this.user ?? { uuid: this.userUuid });
  }

  statusBadgeClass(status: string | undefined = ''): Record<string, boolean> {
    const s = status;
    return {
      'text-success bg-success-light': s === 'Active',
      'text-secondary bg-secondary-light': s === 'InActive',
      'text-danger bg-danger-light': s === 'Blocked',
      'text-warning bg-warning-light': s === 'Suspended' || s === 'Locked',
    };
  }

  agentStatusBadgeClass(status: string | undefined): Record<string, boolean> {
    const s = (status ?? '').toUpperCase();
    return {
      'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/50':
        s === 'ACTIVE' || s === 'DEPLOYED',
      'text-sky-700 bg-sky-100 dark:text-sky-300 dark:bg-sky-950/50': s === 'DRAFT',
      'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-white/5': s === 'INACTIVE',
    };
  }

  productName(agent: AgentRecord): string {
    return agent.product?.name ?? '—';
  }

  clientName(agent: AgentRecord): string {
    return agent.tenant?.tenant_name ?? '—';
  }
}
